import { Link } from "react-router-dom";
import { Star, History, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWatchlist, useRecentlyViewed } from "@/hooks/useUserTokens";
import { useLivePrices } from "@/hooks/useLivePrices";
import { cn } from "@/lib/utils";

const fmtAud = (v?: number) => {
  if (v == null || !isFinite(v)) return "—";
  if (v >= 1000) return `A$${v.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
  if (v >= 1) return `A$${v.toFixed(2)}`;
  return `A$${v.toFixed(v < 0.01 ? 6 : 4)}`;
};

/**
 * Signed-in personal strip. Reads watchlist + recently viewed from the backend
 * (never local storage) so it looks identical on phone and desktop.
 */
export const PersonalStrip = () => {
  const { user } = useAuth();
  const { items: pinned, isLoading } = useWatchlist();
  const { data: recent } = useRecentlyViewed();

  const symbols = [
    ...pinned.map((p) => p.token_symbol.toUpperCase()),
    ...(recent ?? []).map((r) => r.token_symbol.toUpperCase()),
  ];
  const { prices } = useLivePrices(symbols);

  if (!user) return null;

  const displayName = user.email?.split("@")[0] ?? "there";

  return (
    <section className="border-b border-border bg-secondary/30">
      <div className="container mx-auto px-4 py-3 space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="type-eyebrow text-primary">Your watchlist</span>
          <span className="text-xs text-muted-foreground">Signed in as {displayName}</span>
          <Link to="/watchlist" className="text-xs text-primary hover:underline ml-auto">
            Manage watchlist
          </Link>
          <Link to="/account" className="text-xs text-muted-foreground hover:text-foreground">
            Account
          </Link>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : pinned.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Nothing pinned yet — tap the star on any coin to save it here.
            </p>
          ) : (
            pinned.map((p) => {
              const sym = p.token_symbol.toUpperCase();
              const live = prices.get(sym);
              const ch = live?.change24h;
              return (
                <Link
                  key={sym}
                  to={`/coin-reviewer?symbol=${sym}`}
                  className="shrink-0 flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 hover:border-primary/50 transition-colors"
                >
                  <Star className="w-3 h-3 text-warning" fill="currentColor" />
                  <span className="type-data-sm">{sym}</span>
                  <span className="text-xs text-muted-foreground">{fmtAud(live?.price)}</span>
                  {ch != null && (
                    <span
                      className={cn(
                        "text-xs font-semibold",
                        ch > 0 ? "text-success" : ch < 0 ? "text-destructive" : "text-muted-foreground",
                      )}
                    >
                      {ch > 0 ? "+" : ""}
                      {ch.toFixed(1)}%
                    </span>
                  )}
                </Link>
              );
            })
          )}
        </div>

        {recent && recent.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pt-0.5">
            <span className="shrink-0 flex items-center gap-1 type-eyebrow text-muted-foreground">
              <History className="w-3 h-3" /> Recently viewed
            </span>
            {recent.map((r) => {
              const sym = r.token_symbol.toUpperCase();
              return (
                <Link
                  key={sym}
                  to={`/coin-reviewer?symbol=${sym}`}
                  className="shrink-0 rounded-full bg-card border border-border/70 px-2.5 py-1 type-data-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  {sym}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PersonalStrip;
